/**
 * Video Redirect Page - Handles short URLs like /v/abc123
 * Clean preview page before redirecting to actual video
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, Play, AlertCircle, Clock, Download, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function VideoRedirect() {
  const { code } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorType, setErrorType] = useState(null);
  const [videoInfo, setVideoInfo] = useState(null);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    const fetchAndRedirect = async () => {
      try {
        const infoRes = await fetch(`${API_URL}/api/v/${code}/info`);
        
        if (infoRes.status === 404) {
          setError('Video no encontrado');
          setErrorType('notfound');
          setLoading(false);
          return;
        }
        
        if (infoRes.status === 410) {
          setError('El video ha expirado');
          setErrorType('expired');
          setLoading(false);
          return;
        }
        
        if (infoRes.ok) {
          const info = await infoRes.json();
          setVideoInfo(info);
          
          if (info.status !== 'ready') {
            setError('Video aún procesándose');
            setErrorType('processing');
            setLoading(false);
            return;
          }
        }
        
        setLoading(false);
        setRedirecting(true);
        
        // Redirect after showing preview
        setTimeout(() => {
          window.location.href = `${API_URL}/api/v/${code}`;
        }, 2000);
        
      } catch (err) {
        console.error('Video redirect error:', err);
        setError('Error al cargar el video');
        setErrorType('error');
        setLoading(false);
      }
    };

    if (code) {
      fetchAndRedirect();
    }
  }, [code]);

  // Error states
  if (error) {
    return (
      <div
        className="min-h-screen bg-black flex items-center justify-center p-4"
        data-testid="video-redirect-error-state"
      >
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-md"
        >
          {errorType === 'expired' ? (
            <>
              <div className="w-20 h-20 rounded-full bg-yellow-500/10 flex items-center justify-center mx-auto mb-6">
                <Clock className="w-10 h-10 text-yellow-500" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Video Expirado</h1>
              <p className="text-gray-400 mb-6">
                Los videos se almacenan por tiempo limitado. Este video ya no está disponible.
              </p>
            </>
          ) : errorType === 'processing' ? (
            <>
              <div className="w-20 h-20 rounded-full bg-blue-500/10 flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Procesando Video</h1>
              <p className="text-gray-400 mb-6">
                El video aún se está procesando. Intenta de nuevo en unos momentos.
              </p>
            </>
          ) : (
            <>
              <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-10 h-10 text-red-500" />
              </div>
              <h1
                className="text-2xl font-bold text-white mb-2"
                data-testid="video-redirect-error-title"
              >
                {error}
              </h1>
              <p className="text-gray-400 mb-6">
                El enlace puede haber expirado o no existe
              </p>
            </>
          )}
          <Button 
            onClick={() => window.location.href = '/'}
            className="bg-primary hover:bg-primary/90"
            data-testid="video-redirect-home-button"
          >
            Ir al inicio
          </Button>
        </motion.div>
      </div>
    );
  }

  // Loading/Preview state
  return (
    <div
      className="min-h-screen bg-black flex flex-col items-center justify-center p-4"
      data-testid="video-redirect-loading-state"
    >
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center max-w-sm w-full"
      >
        {/* Animated Play Icon */}
        <motion.div 
          className="w-24 h-24 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center mx-auto mb-6 relative"
          animate={redirecting ? { scale: [1, 1.1, 1] } : {}}
          transition={{ repeat: Infinity, duration: 1.5 }}
        >
          <div className="absolute inset-0 rounded-full bg-primary/20 animate-ping" style={{ animationDuration: '2s' }} />
          <Play className="w-12 h-12 text-primary fill-primary/30" />
        </motion.div>
        
        {/* Title */}
        <h1 className="text-2xl font-bold text-white mb-2">Clipper Replay</h1>
        
        {/* Video Info */}
        {videoInfo && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mb-6"
          >
            <p className="text-lg text-gray-300 font-medium">{videoInfo.club_name}</p>
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mt-1">
              <span>{videoInfo.duration}s</span>
              <span>•</span>
              <span>{videoInfo.is_fast ? 'Rápido 2x' : 'Normal'}</span>
            </div>
          </motion.div>
        )}
        
        {/* Loading indicator */}
        {loading ? (
          <div className="flex items-center justify-center gap-3 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Cargando...</span>
          </div>
        ) : redirecting ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
          >
            <div className="flex items-center justify-center gap-3 text-primary">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Abriendo video...</span>
            </div>
            <p className="text-xs text-gray-600">
              Serás redirigido automáticamente
            </p>
          </motion.div>
        ) : null}
        
        {/* Manual actions (shown after brief delay) */}
        {!loading && videoInfo && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
            className="mt-8 space-y-3"
          >
            <Button
              onClick={() => window.location.href = `${API_URL}/api/v/${code}`}
              className="w-full bg-primary hover:bg-primary/90"
            >
              <Download className="w-4 h-4 mr-2" />
              Descargar Video
            </Button>
            
            <Button
              variant="outline"
              onClick={() => {
                if (navigator.share) {
                  navigator.share({
                    title: 'Clipper Replay',
                    text: `¡Mira mi replay de ${videoInfo.club_name}!`,
                    url: window.location.href
                  });
                } else {
                  navigator.clipboard.writeText(window.location.href);
                }
              }}
              className="w-full border-gray-700 text-gray-300 hover:bg-gray-800"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Compartir
            </Button>
          </motion.div>
        )}
      </motion.div>
      
      {/* Branding footer */}
      <div className="absolute bottom-6 text-center">
        <p className="text-xs text-gray-700">
          Powered by Clipper Sports Replay
        </p>
      </div>
    </div>
  );
}
